import { useState, useEffect } from 'react';
import { checkUrl } from '../adminApi';
import { adminFetch } from '../adminFetch';

/**
 * Checks whether the currently logged-in user is an admin of a given product
 * ('pnc' | 'shopmanual'). Admin status is per-product (each backend has its own
 * users/is_admin), so the result is cached per context and cleared on logout
 * (logout clears sessionStorage).
 *
 * This decides only what the UI offers; it is not a security boundary. The
 * admin endpoints authorize every request themselves against the identity the
 * gateway injects, so tampering with the cached answer here buys nothing.
 *
 * Returns { isAdmin: bool | null, loading: bool }  (isAdmin is null while loading)
 */
export default function useIsAdmin(context = 'pnc') {
  const [isAdmin,  setIsAdmin]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const userId = sessionStorage.getItem('user_id') || localStorage.getItem('user_id');

    if (!userId) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // Cache per product to avoid repeated API calls within a session.
    const cacheKey = `is_admin_${context}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached !== null) {
      setIsAdmin(cached === 'true');
      setLoading(false);
      return;
    }

    setLoading(true);
    // user_id is still sent because the backend answers for the caller it can
    // verify; a 403 here just means "not an admin", which is the same answer.
    adminFetch(`${checkUrl(context)}?user_id=${encodeURIComponent(userId)}`)
      .then(r => {
        if (r.status === 401 || r.status === 403) return { is_admin: false };
        if (!r.ok) throw new Error('check failed');
        return r.json();
      })
      .then(d => {
        const val = !!d.is_admin;
        sessionStorage.setItem(cacheKey, String(val));
        setIsAdmin(val);
        setLoading(false);
      })
      .catch(() => {
        // On error assume not admin — do not cache so it retries on next mount
        setIsAdmin(false);
        setLoading(false);
      });
  }, [context]);

  return { isAdmin, loading };
}
